import { useState, type FormEvent, type ReactNode } from "react";
import type { Api } from "../data/api.ts";
import { Field, Logo, navigate, toast } from "../ui/kit.tsx";

function Card({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-card">
        <a href="https://ordopli.fr" className="auth-brand"><Logo size={34} /> Ordopli</a>
        <h1>{title}</h1>
        {sub && <p className="muted">{sub}</p>}
        {children}
      </div>
      <div className="auth-cmjn" aria-hidden="true"><i /><i /><i /><i /></div>
    </div>
  );
}

export function AuthPages({ api, route, onDemo, notice = "" }: { api: Api; route: string[]; onDemo: () => void; notice?: string }) {
  if (route[0] === "inscription") return <Signup api={api} />;
  if (route[0] === "mot-de-passe") return <Reset api={api} />;
  return <Login api={api} onDemo={onDemo} notice={notice} />;
}

function Login({ api, onDemo, notice }: { api: Api; onDemo: () => void; notice: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(notice);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.signIn(email.trim(), password); navigate("planning"); }
    catch (x) { setErr((x as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <Card title="Connexion" sub="Planification de l'atelier de façonnage.">
      <form className="stack" onSubmit={submit}>
        <Field label="Adresse e-mail"><input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
        <Field label="Mot de passe"><input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
        {err && <p className="form-error">{err}</p>}
        <button className="btn btn-primary btn-block" disabled={busy}>{busy ? "Connexion…" : "Se connecter"}</button>
      </form>
      <div className="auth-links">
        <a href="#/mot-de-passe">Mot de passe oublié ?</a>
        <a href="#/inscription">Créer un compte</a>
      </div>
      <div className="auth-demo">
        <span>Pas encore de compte ?</span>
        <button className="btn" onClick={onDemo}>Essayer la démonstration</button>
      </div>
    </Card>
  );
}

function Signup({ api }: { api: Api }) {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr("");
    if (f.password.length < 8) { setErr("Le mot de passe doit faire au moins 8 caractères."); return; }
    setBusy(true);
    try {
      const r = await api.signUp(f.email.trim(), f.password, f.name.trim());
      if (r === "confirmer") setDone(true); else navigate("planning");
    } catch (x) { setErr((x as Error).message); }
    finally { setBusy(false); }
  };
  if (done) {
    return (
      <Card title="Vérifiez vos mails" sub={`Un lien de confirmation a été envoyé à ${f.email}. Cliquez dessus, puis connectez-vous.`}>
        <a className="btn btn-primary btn-block" href="#/connexion">Aller à la connexion</a>
      </Card>
    );
  }
  return (
    <Card title="Créer un compte" sub="Vous paramétrerez votre imprimerie juste après.">
      <form className="stack" onSubmit={submit}>
        <Field label="Votre nom"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required autoComplete="name" /></Field>
        <Field label="Adresse e-mail professionnelle"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required autoComplete="email" /></Field>
        <Field label="Mot de passe" hint="8 caractères minimum"><input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required autoComplete="new-password" /></Field>
        {err && <p className="form-error">{err}</p>}
        <button className="btn btn-primary btn-block" disabled={busy}>{busy ? "Création…" : "Créer mon compte"}</button>
      </form>
      <div className="auth-links"><a href="#/connexion">J'ai déjà un compte</a></div>
    </Card>
  );
}

function Reset({ api }: { api: Api }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr("");
    try { await api.resetPassword(email.trim()); setSent(true); } catch (x) { setErr((x as Error).message); }
  };
  return (
    <Card title="Mot de passe oublié" sub={sent ? "Si un compte existe avec cette adresse, un lien pour choisir un nouveau mot de passe vient de partir." : "Indiquez votre adresse : vous recevrez un lien par mail."}>
      {!sent && (
        <form className="stack" onSubmit={submit}>
          <Field label="Adresse e-mail"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
          {err && <p className="form-error">{err}</p>}
          <button className="btn btn-primary btn-block">Envoyer le lien</button>
        </form>
      )}
      <div className="auth-links"><a href="#/connexion">Retour à la connexion</a></div>
    </Card>
  );
}

export function CreateOrgPage({ api, onCreated }: { api: Api; onCreated: () => Promise<void> }) {
  const [f, setF] = useState({ name: "", siret: "", user: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.createOrg(f.name.trim(), f.siret.trim(), f.user.trim()); await onCreated(); navigate("parametres/machines"); }
    catch (x) { setErr((x as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <Card title="Votre imprimerie" sub="Dernière étape avant de paramétrer votre atelier.">
      <form className="stack" onSubmit={submit}>
        <Field label="Nom de l'imprimerie"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required autoComplete="organization" /></Field>
        <Field label="SIRET" hint="Facultatif pendant la phase pilote"><input value={f.siret} onChange={(e) => setF({ ...f, siret: e.target.value })} inputMode="numeric" /></Field>
        <Field label="Votre nom"><input value={f.user} onChange={(e) => setF({ ...f, user: e.target.value })} required autoComplete="name" /></Field>
        {err && <p className="form-error">{err}</p>}
        <button className="btn btn-primary btn-block" disabled={busy}>Continuer</button>
      </form>
      <div className="auth-links"><button className="link" onClick={() => api.signOut()}>Se déconnecter</button></div>
    </Card>
  );
}

export function NewPasswordPage({ api, message }: { api: Api; message?: string }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8) { setErr("Le mot de passe doit faire au moins 8 caractères."); return; }
    if (pw !== pw2) { setErr("Les deux mots de passe ne sont pas identiques."); return; }
    setBusy(true);
    try { await api.updatePassword(pw); toast("Mot de passe enregistré."); navigate("planning"); }
    catch (x) { setErr((x as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <Card title={message?.startsWith("Bienvenue") ? "Bienvenue sur Ordopli" : "Nouveau mot de passe"} sub={message || `Compte ${api.session()?.email ?? ""}`}>
      <form className="stack" onSubmit={submit}>
        <Field label="Nouveau mot de passe" hint="8 caractères minimum"><input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required autoFocus /></Field>
        <Field label="Confirmer"><input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required /></Field>
        {err && <p className="form-error">{err}</p>}
        <button className="btn btn-primary btn-block" disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
      </form>
    </Card>
  );
}
